import React, { useState, useEffect, useCallback } from 'react';
import {
  Tree, Button, Space, App, Popconfirm, Modal, Form, Input, Select, InputNumber, TreeSelect, Card, Typography, Spin, Row, Col
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import {
  getAllMenus,
  deleteMenu,
  createMenu,
  updateMenu,
} from '../../services/menuService';
import {
  getMasterPermissions
} from '../../services/masterDataService';
import { createPermission } from '../../services/permissionService';
import { MenuItem, Permission } from '../../types/menu';
import { useAuth } from '../../contexts/AuthContext';
import { iconList, iconMap } from '../../utils/antdIcons';

const { Option } = Select;
const { Title } = Typography;

// Helper component to render an icon dynamically by name
const DynamicAntdIcon: React.FC<{ name: string }> = ({ name }) => {
  const IconComponent = iconMap[name];
  return IconComponent ? <IconComponent /> : null;
};

const mapApiDataToTreeData = (items: MenuItem[]): any[] => {
  return items
    .sort((a, b) => a.Order - b.Order)
    .map(item => ({
      ...item,
      key: item.id,
      value: item.id,
      title: item.Name,
      children: item.Children && item.Children.length > 0 ? mapApiDataToTreeData(item.Children) : [],
    }));
};

const MenuManagement: React.FC = () => {
  const [menus, setMenus] = useState<MenuItem[]>([]);
  const [treeData, setTreeData] = useState<any[]>([]);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [permissionLoading, setPermissionLoading] = useState(false);

  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingMenu, setEditingMenu] = useState<MenuItem | null>(null);

  const [form] = Form.useForm();
  const { refreshMenu, hasPermission } = useAuth();
  const { message } = App.useApp();

  const canCreate = hasPermission('menu.create');
  const canUpdate = hasPermission('menu.update');
  const canDelete = hasPermission('menu.delete');

  const fetchAllData = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch Menus
      let menusFromApi = [];
      try {
        const menusRes = await getAllMenus();
        menusFromApi = menusRes.data.data || [];
        setMenus(menusFromApi);
        setTreeData(mapApiDataToTreeData(menusFromApi));
      } catch (error) {
        console.error("Failed to fetch menus:", error);
        message.error('Gagal mengambil data menu. Silakan periksa konsol untuk detail.');
        // Stop execution if menus fail to load, as they are critical
        return;
      }

      // Fetch Permissions
      try {
        const permissionsRes = await getMasterPermissions();
        setPermissions(permissionsRes.data.data || []);
      } catch (error) {
        console.error("Failed to fetch permissions:", error);
        message.error('Gagal mengambil data permission. Silakan periksa konsol untuk detail.');
      }

    } catch (error) {
      // This is a fallback, but the specific catches above should handle errors
      console.error("An unexpected error occurred:", error);
      message.error('Terjadi kesalahan tak terduga saat mengambil data.');
    } finally {
      setLoading(false);
    }
  }, [message]);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  const showAddModal = () => {
    setEditingMenu(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  const showAddChildModal = (parentNode: MenuItem) => {
    setEditingMenu(null);
    form.resetFields();
    // Pre-fill Parent ID and the new Path field
    const parentPath = parentNode.Path ? `${parentNode.Path}/` : '/';
    form.setFieldsValue({
      parent_id: parentNode.id,
      Path: parentPath,
    });
    setIsModalVisible(true);
  };

  const showEditModal = (record: MenuItem) => {
    setEditingMenu(record);
    form.setFieldsValue({
      ...record,
      parent_id: record.ParentID || null,
      permission_names: record.Permissions?.map((p: any) => ({ value: p.id, label: p.name })) || [],
    });
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    setEditingMenu(null);
  };

  const handleOk = async () => {
    console.log('Attempting to save menu...');
    try {
      const values = await form.validateFields();
      console.log('Raw form input values:', values); // Log semua input dari form

      const { permission_names, ...rest } = values;

      // Convert permission names to permission IDs
      const extractedPermissionIds = permission_names?.map((item: { value: string; label: string }) => item.value) || [];

      // Auto-generate key from name if it doesn't exist
      const keyFromName = values.Name
        ? values.Name.toLowerCase().replace(/\s+/g, '_')
        : '';

      const payload = {
        ...rest,
        key: values.Key || keyFromName,
        permission_ids: extractedPermissionIds,
      };

      console.log('Menu payload:', payload);

      if (editingMenu) {
        await updateMenu(editingMenu.id, payload);
        message.success('Item menu berhasil diperbarui');
      } else {
        await createMenu(payload);
        message.success('Item menu berhasil dibuat');
      }
      await refreshMenu(); // <-- Add this line
      setIsModalVisible(false);
      setEditingMenu(null);
      fetchAllData();
    } catch (error: any) {
      console.error("Validation failed or error saving menu item:", error);
      const errorMessage = error.response?.data?.message || 'Gagal menyimpan item menu';
      message.error(errorMessage);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteMenu(id);
      message.success('Item menu berhasil dihapus');
      fetchAllData();
    } catch (error) {
      message.error('Gagal menghapus item menu');
    }
  };

  const handlePermissionsChange = async (values: { label: string; value: string }[]) => {
    const newTag = values.find(v => !permissions.map(p => p.id).includes(v.value));

    if (!newTag) {
      form.setFieldsValue({ permission_names: values });
      return;
    }

    const newPermissionName = newTag.value;
    setPermissionLoading(true);

    // Function to format the display name
    const formatDisplayName = (name: string): string => {
      if (!name.includes('.')) {
        return name.charAt(0).toUpperCase() + name.slice(1);
      }
      const [module, action] = name.split('.');
      const formattedModule = module.charAt(0).toUpperCase() + module.slice(1);
      const formattedAction = action.charAt(0).toUpperCase() + action.slice(1);
      return `${formattedAction} ${formattedModule}`;
    };

    try {
      const displayName = formatDisplayName(newPermissionName);
      const payload = {
        name: newPermissionName,
        display_name: displayName,
        description: displayName,
        module: newPermissionName.split('.')[0] || 'general',
      };
      console.log('Attempting to create permission with payload:', payload);
      const res = await createPermission(payload);
      const newPermission = res.data.data;
      message.success(`Permission "${newPermission.display_name}" berhasil dibuat`);

      const updatedPermissions = [...permissions, newPermission];
      setPermissions(updatedPermissions);

      const newFormValues = values.map(v =>
        v.value === newPermissionName
          ? { label: newPermission.name, value: newPermission.id }
          : v
      );
      form.setFieldsValue({ permission_names: newFormValues });

    } catch (error: any) {
      console.error("Failed to create permission:", error.response?.data || error.message);
      const errorMessage = error.response?.data?.message || 'Gagal membuat permission';
      message.error(errorMessage);
      const failedFormValues = values.filter(v => v.value !== newPermissionName);
      form.setFieldsValue({ permission_names: failedFormValues });
    } finally {
      setPermissionLoading(false);
    }
  };

  const titleRenderer = (nodeData: any) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
      <span>{nodeData.Name} ({nodeData.Path || 'Induk'})</span>
      <Space>
        {canCreate && (
          <Button
            type="text"
            icon={<PlusOutlined />}
            onClick={(e) => { e.stopPropagation(); showAddChildModal(nodeData); }}
          />
        )}
        {canUpdate && (
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={(e) => { e.stopPropagation(); showEditModal(nodeData); }}
          />
        )}
        {canDelete && (
          <Popconfirm
            title="Apakah Anda yakin ingin menghapus ini?"
            onConfirm={(e) => { e?.stopPropagation(); handleDelete(nodeData.id); }}
            onCancel={(e) => e?.stopPropagation()}
            okText="Ya"
            cancelText="Tidak"
          >
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              onClick={(e) => e.stopPropagation()}
            />
          </Popconfirm>
        )}
      </Space>
    </div>
  );

  const parentMenuTreeDataForSelect = mapApiDataToTreeData(menus);

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={2} style={{ margin: 0 }}>
            Manajemen Menu
          </Title>
        </Col>
        {canCreate && (
          <Col>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={showAddModal}
            >
              Tambah Menu
            </Button>
          </Col>
        )}
      </Row>
      <Card>
        {loading ? <Spin /> : (
          <Tree
            treeData={treeData}
            titleRender={titleRenderer}
            blockNode
            defaultExpandAll
          />
        )}
      </Card>

      <Modal
        title={editingMenu ? 'Edit Item Menu' : 'Tambah Item Menu'}
        visible={isModalVisible}
        onOk={handleOk}
        onCancel={handleCancel}
        destroyOnClose
      >
        <Form form={form} layout="vertical" name="menuForm" initialValues={{ Order: 10 }}>
          <Form.Item name="Name" label="Nama" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="Path" label="Path">
            <Input placeholder="contoh: /admin/users" />
          </Form.Item>
          <Form.Item name="Icon" label="Icon">
            <Select
              showSearch
              placeholder="Pilih icon"
              filterOption={(input, option) =>
                String(option?.value ?? '').toLowerCase().includes(input.toLowerCase())
              }
            >
              {iconList.map(iconName => (
                <Option key={iconName} value={iconName}>
                  <DynamicAntdIcon name={iconName} />
                  <span style={{ marginLeft: 8 }}>{iconName}</span>
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="Order" label="Urutan" rules={[{ required: true }]} hidden>
            <InputNumber style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="parent_id" label="Menu Induk" hidden>
            <TreeSelect
              treeData={parentMenuTreeDataForSelect}
              placeholder="Pilih menu induk"
              allowClear
              treeDefaultExpandAll
            />
          </Form.Item>

          <Form.Item
            label="Permission"
            name="permission_names"
            tooltip="Pilih permission yang ada atau ketik yang baru lalu tekan Enter untuk membuatnya."
          >
            <Select
              labelInValue
              mode="tags"
              placeholder="Ketik permission lalu tekan Enter"
              onChange={handlePermissionsChange}
              tokenSeparators={[',']}
              loading={permissionLoading}
              showArrow={false}
              style={{ width: '100%' }}
              open={false}
              options={permissions.map(p => ({ label: p.name, value: p.id }))}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default MenuManagement;